import Checkbox from '@mui/material/Checkbox';
import type { CheckboxProps } from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import type { FormControlLabelProps } from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import {
	FieldPath,
	FieldValues,
	UseControllerProps,
	useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { displayError, getHelperText } from './functions';

export type ControlledCheckboxProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<CheckboxProps, 'name'> &
	Pick<UseControllerProps<TFieldValues, TName>, 'name' | 'control'> & {
		validation?: UseControllerProps<TFieldValues, TName>['rules'];
		label?: FormControlLabelProps['label'];
		helperText?: string;
	};

export const ControlledCheckbox = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	validation = {},
	required,
	disabled,
	label,
	control,
	helperText,
	sx,
	...rest
}: ControlledCheckboxProps<TFieldValues, TName>): React.JSX.Element => {
	const { t } = useTranslation();

	if (Boolean(validation.required) && !required) {
		required = true;
	}

	const {
		field: { onChange, value },
		fieldState: { error },
	} = useController<TFieldValues, TName>({
		name,
		control,
		rules: validation,
	});

	const parsedHelperText = getHelperText(t, disabled, error, helperText);
	const isErrored = displayError(disabled, error);
	const { size } = rest;

	return (
		<FormControl required={required} disabled={disabled} error={isErrored}>
			<FormGroup row>
				<FormControlLabel
					sx={label ? {} : { margin: 0 }}
					control={
						<Checkbox
							{...rest}
							sx={[
								...(Array.isArray(sx) ? sx : [sx]),
								{
									color: isErrored ? 'error.main' : 'inherit',
								},
							]}
							value={value}
							checked={!!value}
							onChange={(_, checked: boolean) => {
								onChange(checked);
							}}
						/>
					}
					label={
						<Typography
							variant={size === 'small' ? 'body2' : 'body1'}>
							{label}
							{required && (
								<Typography component="span" color="error">
									{' *'}
								</Typography>
							)}
						</Typography>
					}
				/>
			</FormGroup>
			{parsedHelperText && (
				<FormHelperText error={isErrored}>
					{parsedHelperText}
				</FormHelperText>
			)}
		</FormControl>
	);
};
